'use client';

import React, { memo, useState } from 'react';
import R2Image from '@/components/ui/R2Image';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import RichText from '@/components/ui/RichText/RichText';

// Icons moved outside to prevent re-creation
const EmailIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const NewsletterSection: React.FC<any> = ({ data }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { ref, sectionClasses, childClasses, getChildStyle } =
    useScrollAnimation();

  if (!data) return null;

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          result?.message ||
            'Newsletter subscription failed. Please try again.',
        );
      }

      setEmail('');
      alert('Thank you for subscribing!');
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : 'Newsletter subscription failed. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const subscribeButton = data.buttonsCollection?.items.find(
    (btn: any) => btn.functionality === 'subscribe' || btn.slug === 'subscribe',
  ) || { sys: { id: 'default-sub' }, title: 'Subscribe', slug: 'subscribe' };

  return (
    <section
      ref={ref}
      className={`overflow-hidden pb-8 md:pb-10 ${sectionClasses}`}
      data-testid="newsletter-section"
    >
      <div className="content-wrapper bg-surface-light">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          {/* Illustration Area */}
          <div
            className={`flex justify-center ${childClasses()}`}
            style={getChildStyle(200)}
          >
            {data.backgroundImage?.url ? (
              <R2Image
                path={data.backgroundImage.url}
                alt={data.backgroundImage.title || 'Newsletter illustration'}
                width={448}
                height={384}
                className="h-auto w-full max-w-md transform transition-transform duration-500 hover:scale-110 hover:rotate-2"
              />
            ) : (
              /* Fallback Phone Illustration */
              <div className="relative h-96 w-56 rounded-[3rem] border-4 border-gray-800 bg-gray-900 p-3 shadow-2xl">
                <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[2.5rem] bg-white p-6 text-center">
                  <div className="mb-4 flex h-16 w-20 items-center justify-center rounded-2xl bg-indigo-600 shadow-lg">
                    <EmailIcon className="h-10 w-10 text-white" />
                  </div>
                  <div className="mb-2 h-2 w-24 rounded-full bg-gray-200" />
                  <div className="h-2 w-16 rounded-full bg-gray-100" />
                </div>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="space-y-8">
            <div className={childClasses()} style={getChildStyle(400)}>
              <RichText
                styles={{
                  p: 'text-4xl md:text-5xl font-bold text-theme-brand-blue leading-[1.1]',
                }}
              >
                {data.heading}
              </RichText>
            </div>

            <div className={childClasses()} style={getChildStyle(600)}>
              <RichText
                styles={{
                  className: 'text-lg text-gray-600 leading-relaxed',
                }}
              >
                {data.description}
              </RichText>
            </div>

            <form
              onSubmit={handleSubmit}
              className={`max-w-md space-y-4 ${childClasses()}`}
              style={getChildStyle(800)}
            >
              <div className="group relative">
                <label htmlFor="newsletter-email" className="sr-only">
                  Email Address
                </label>
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                  <EmailIcon className="h-5 w-5 text-gray-400 transition-colors group-focus-within:text-indigo-500" />
                </div>
                <input
                  id="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full rounded-2xl border border-gray-200 bg-white py-4 pr-4 pl-12 transition-all outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50/50"
                  required
                />
              </div>

              <div className="relative">
                <PrimaryButton
                  data={subscribeButton}
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full justify-center py-4 text-lg ${isSubmitting ? 'opacity-80' : ''}`}
                />
                {isSubmitting && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-3 border-white/30 border-t-white" />
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(NewsletterSection);
